import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  type?: 'bug' | 'feature_request' | 'support' | 'churn_signal' | 'general';
  assigneeId?: string | null; // null means unassign
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  updateAssignee?: boolean;
  tags?: string[];
  dueDate?: string;
  /** When true, clears due date (backend cannot infer null from omitted JSON fields). */
  clearDueDate?: boolean;
  estimatedHours?: number;
  actualHours?: number;

  // Column and position changes (explicit API for better validation)
  fromColumnId?: string; // Current column (for validation)
  toColumnId?: string; // Target column
  fromPosition?: number; // Current position (optional)
  toPosition?: number; // Target position

  // Legacy support (deprecated)
  columnId?: string;
  position?: number;

  metadata?: Record<string, any>;

  /** Replace linked audience members (customers). Omit to leave unchanged; send `[]` to clear. */
  audienceMemberIds?: string[];

  archived?: boolean;
}

export interface UpdateTicketResponse {
  success: boolean;
  data: {
    ticketId: string;
    updatedAt: string;
    updatedBy: string;
    updatedByName: string;
  };
}

async function updateTicket(
  ticketId: string,
  data: UpdateTicketRequest,
  sessionJwt: string | undefined,
  workspaceId: string,
) {
  const bearer = resolveBearerToken(sessionJwt);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.put<UpdateTicketResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}`,
    data,
    { headers },
  );

  return response.data;
}

export function useUpdateTicket() {
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: UpdateTicketRequest }) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return updateTicket(ticketId, data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async ({ ticketId, data }) => {
      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically update the cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic update: Moving ticket', ticketId);

        // Clone the data to avoid mutating the cache directly
        const newTickets = old.tickets.map((t: any) => {
          if (t.ticketId === ticketId || t.id === ticketId) {
            const { clearDueDate, archived, ...patch } = data;
            const merged = { ...t, ...patch };
            if (clearDueDate) {
              merged.dueDate = undefined;
            }
            if (archived !== undefined) {
              merged.archived = archived;
            }
            return merged;
          }
          return t;
        });

        // If it's a reorder/move operation, also update column ticketOrder arrays and ticket columnId
        if (
          (data.fromColumnId && data.toColumnId && data.toPosition !== undefined) ||
          (data.columnId && data.position !== undefined)
        ) {
          const fromColId = data.fromColumnId || data.columnId;
          const toColId = data.toColumnId || data.columnId;
          const toPos = data.toPosition ?? data.position;

          // Update ticket's columnId property immediately
          const updatedTickets = newTickets.map((t: any) => {
            if ((t.ticketId === ticketId || t.id === ticketId) && toColId) {
              return { ...t, columnId: toColId };
            }
            return t;
          });

          const newColumns = old.columns.map((col: any) => ({
            ...col,
            ticketOrder: col.ticketOrder ? [...col.ticketOrder] : [],
          }));

          const fromColumn = newColumns.find((c: any) => c.id === fromColId);
          const toColumn = newColumns.find((c: any) => c.id === toColId);

          if (fromColumn && toColumn) {
            // Remove from old column's ticketOrder
            const ticketIndex = fromColumn.ticketOrder?.indexOf(ticketId);
            if (ticketIndex !== undefined && ticketIndex > -1) {
              fromColumn.ticketOrder.splice(ticketIndex, 1);
            }

            // Add to new column's ticketOrder at the correct position
            if (!toColumn.ticketOrder) {
              toColumn.ticketOrder = [];
            }
            // Ensure position is within bounds
            const insertPosition = Math.min(toPos!, toColumn.ticketOrder.length);
            toColumn.ticketOrder.splice(insertPosition, 0, ticketId);

            console.log(
              `🔮 Optimistic: Moved ${ticketId} from column ${fromColId} to column ${toColId}[${insertPosition}]`,
            );
          }

          console.log(`🔮 Optimistic: Updated ticket properties, columnId, and column order`);
          return { ...old, tickets: updatedTickets, columns: newColumns };
        }

        // For other updates (title, description, etc.), just update the ticket properties
        console.log(`🔮 Optimistic: Updated ticket ${ticketId} properties (no refetch needed)`);
        return { ...old, tickets: newTickets };
      });

      // Return context with the snapshot
      return { previousTickets };
    },
    onSuccess: async (data, variables) => {
      console.log('✅ Ticket updated successfully:', data);

      const patch = variables.data;
      const updatedFields = (Object.keys(patch) as (keyof UpdateTicketRequest)[]).filter(
        (k) => patch[k] !== undefined,
      );
      trackEvent('ticket_updated', {
        ticket_id: variables.ticketId,
        updated_fields: updatedFields,
      });

      // Only invalidate for reorder/move operations (critical for data consistency)
      // For simple property updates (title, description), optimistic update is sufficient
      const isReorderOrMove =
        (variables.data.fromColumnId && variables.data.toColumnId) ||
        (variables.data.columnId && variables.data.position !== undefined);

      const audienceLinksChanged = variables.data.audienceMemberIds !== undefined;
      const archiveChanged = variables.data.archived !== undefined;
      const dueDateChanged =
        variables.data.dueDate !== undefined || variables.data.clearDueDate === true;

      if (isReorderOrMove || audienceLinksChanged || archiveChanged || dueDateChanged) {
        console.log('🔄 Invalidating tickets query (reorder/move or audience links)');
        await queryClient.invalidateQueries({ queryKey: [queryKeys.tickets, workspaceId] });
      } else {
        console.log('✅ Optimistic update sufficient, skipping refetch for simple property update');
        // The optimistic update already applied the changes, no need to refetch
      }

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.ticketActivity, variables.ticketId, workspaceId],
      });
    },
    onError: (err: any, variables, context: any) => {
      console.error('❌ Ticket update failed:', err);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (err.response?.status === 400) {
        throw new Error('Invalid ticket data. Please check your input and try again.');
      } else if (err.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (err.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (err.response?.status === 404) {
        throw new Error('Ticket not found.');
      } else if (err.response?.status === 409) {
        throw new Error('Ticket was modified by another user. Please refresh and try again.');
      } else if (err.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(err.message || 'Failed to update ticket. Please try again.');
      }
    },
  });
}
